import { Injectable, inject, resource, computed } from '@angular/core';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, serverTimestamp, writeBatch, deleteField, type UpdateData } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { FIRESTORE_TOKEN } from '../firebase/firestore.provider';
import { FUNCTIONS_TOKEN } from '../firebase/functions.provider';
import { AuthService } from '../auth/auth.service';
import { Site, LatestSiteData, LatestNoiseSensorData, SiteListItem, SiteDetail, SiteCreateInput, SiteUpdateInput, SiteAlertSettings } from '../../shared/interfaces';
import { SiteManagementDepartmentService } from './site-management-department.service';
import { SensorManageService } from './sensor-manage.service';
import { AccessLogService } from './access-log.service';
import { mapWindDirection, normalizeWindDirectionCode } from '../../shared/utils/wind-direction';
import { mapNoiseStatusNo, mapSensorStatusNo, getWorstNoiseSensor, getWorstSensor, isSensorStale } from '../../shared/utils/sensor-status';

@Injectable({
  providedIn: 'root'
})
export class SiteService {
  private readonly firestore = inject(FIRESTORE_TOKEN);
  private readonly functions = inject(FUNCTIONS_TOKEN);
  private readonly authService = inject(AuthService);
  private readonly departmentService = inject(SiteManagementDepartmentService);
  private readonly sensorService = inject(SensorManageService);
  private readonly accessLogService = inject(AccessLogService);

  private async logAction(action: string, resourcePath: string, status: 'success' | 'error'): Promise<void> {
    const user = this.authService.user();
    if (!user) return;
    await this.accessLogService.addLog({
      userId: user.uid,
      userName: user.email || '',
      action,
      resource: resourcePath,
      status
    });
  }

  private normalizeNoiseData(noise: LatestNoiseSensorData[] | Record<string, LatestNoiseSensorData> | undefined): LatestNoiseSensorData[] {
    if (!noise) return [];
    if (Array.isArray(noise)) return noise;
    if (this.isLatestNoiseSensorData(noise)) return [noise];
    return Object.entries(noise as Record<string, LatestNoiseSensorData>).map(([id, value]) => ({
      id: value.id ?? id,
      ...value,
    }));
  }

  private isLatestNoiseSensorData(value: unknown): value is LatestNoiseSensorData {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

    const data = value as Record<string, unknown>;
    return typeof data['id'] === 'string'
      || typeof data['positionName'] === 'string'
      || typeof data['noiseMax'] === 'number'
      || typeof data['noiseL5'] === 'number'
      || typeof data['vibrationMax'] === 'number'
      || typeof data['vibrationL10'] === 'number'
      || typeof data['statusNo'] === 'string'
      || typeof data['statusLabel'] === 'string'
      || typeof data['date'] === 'string';
  }

  private readonly sitesResource = resource({
    params: () => ({
      orgId: this.authService.userOrganizationId(),
      role: this.authService.userRole()
    }),
    loader: async ({ params: { orgId, role } }) => {
      if (!orgId && role !== 'super_admin') return [];
      try {
        const sitesRef = collection(this.firestore, 'sites');
        let q;
        if (role === 'super_admin') {
          q = query(sitesRef, where('deletedAt', '==', null));
        } else {
          q = query(
            sitesRef,
            where('organizationId', '==', orgId),
            where('deletedAt', '==', null)
          );
        }
        const snapshot = await getDocs(q);
        return snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          } as Site));
      } catch (err) {
        console.error('現場一覧の取得に失敗しました:', err);
        throw new Error('現場一覧の取得に失敗しました');
      }
    }
  });

  private readonly latestSiteDataResource = resource({
    params: () => ({
      orgId: this.authService.userOrganizationId(),
      role: this.authService.userRole()
    }),
    loader: async ({ params: { orgId, role } }) => {
      if (!orgId && role !== 'super_admin') return [];
      try {
        const ref = collection(this.firestore, 'latest_site_data');
        let q;
        if (role === 'super_admin') {
          q = query(ref);
        } else {
          q = query(ref, where('organizationId', '==', orgId));
        }
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as LatestSiteData));
      } catch (err) {
        console.error('最新現場データの取得に失敗しました:', err);
        throw new Error('最新現場データの取得に失敗しました');
      }
    }
  });

  private mergeSiteWithLatestData(site: Site, latest?: LatestSiteData, hasWbgt?: boolean, hasWind?: boolean, hasNoise?: boolean): SiteDetail {
    // ---------------------------------------------------------------------------
    // 鮮度チェック: 最終更新から1時間以上経過したセンサーは停止扱い
    // ---------------------------------------------------------------------------
    const applyStaleCheck = <T extends { date?: string; statusNo?: string; statusLabel?: string }>(sensors: T[]): T[] =>
      sensors.map(s => isSensorStale(s.date)
        ? { ...s, statusNo: '99', statusLabel: '停止' }
        : s
      );

    // WBGT: 鮮度チェック適用後、最悪ステータスを選択
    const processedWbgt = hasWbgt ? applyStaleCheck(latest?.sensorData?.wbgt ?? []) : [];
    const worstWbgt = hasWbgt ? getWorstSensor(processedWbgt) : undefined;
    const wbgtStatusNo = worstWbgt?.statusNo;
    const wbgtStatus = hasWbgt
      ? (mapSensorStatusNo(wbgtStatusNo) || latest?.siteWbgtStatus || 'stopped')
      : undefined;
    const wbgtStatusLabel = hasWbgt
      ? (worstWbgt?.statusLabel || '停止')
      : undefined;
    const wbgtValue = hasWbgt
      ? (worstWbgt?.wbgt ?? latest?.siteWbgt)
      : undefined;

    // WIND: 鮮度チェック適用後、最悪ステータスを選択
    const processedWind = hasWind ? applyStaleCheck(latest?.sensorData?.wind ?? []) : [];
    const worstWind = hasWind ? getWorstSensor(processedWind) : undefined;
    const windStatusNo = worstWind?.statusNo;
    const windSpeedStatus = hasWind
      ? (mapSensorStatusNo(windStatusNo) || latest?.siteWindSpeedStatus || 'stopped')
      : undefined;
    const windSpeedStatusLabel = hasWind
      ? (worstWind?.statusLabel || '停止')
      : undefined;
    const windDirectionSource = worstWind?.direction ?? latest?.siteWindDirection;
    const windDirectionCode = hasWind ? normalizeWindDirectionCode(windDirectionSource) : undefined;

    // NOISE: 鮮度チェック適用後、NOISE専用深刻度で最悪ステータスを選択
    const processedNoise = hasNoise ? applyStaleCheck(this.normalizeNoiseData(latest?.sensorData?.noise)) : [];
    const worstNoise = hasNoise ? getWorstNoiseSensor(processedNoise) : undefined;
    const noiseStatusNo = worstNoise?.statusNo;
    const noiseStatus = hasNoise
      ? (mapNoiseStatusNo(noiseStatusNo) || 'stopped')
      : undefined;
    const noiseStatusLabel = hasNoise
      ? (worstNoise?.statusLabel || '停止')
      : undefined;

    // 全WBGTセンサーの個別ステータス一覧を構築（鮮度チェック適用済み）
    const wbgtSensors = hasWbgt
      ? processedWbgt.map(s => ({
          id: s.id,
          positionName: s.positionName,
          wbgt: s.wbgt,
          statusNo: s.statusNo,
          statusLabel: s.statusLabel,
          date: s.date,
        }))
      : undefined;

    const noiseSensors = hasNoise
      ? processedNoise.map(s => ({
          id: s.id,
          positionName: s.positionName,
          noiseMax: s.noiseMax,
          noiseL5: s.noiseL5,
          vibrationMax: s.vibrationMax,
          vibrationL10: s.vibrationL10,
          statusNo: s.statusNo,
          statusLabel: s.statusLabel,
          date: s.date,
        }))
      : undefined;

    return {
      ...site,
      siteStatusNo: latest?.siteStatusNo,
      wbgtStatus,
      wbgtStatusNo: hasWbgt ? (wbgtStatusNo || '99') : undefined,
      wbgtStatusLabel,
      wbgtValue,
      wbgtSensors,
      windSpeedStatus,
      windSpeedStatusNo: hasWind ? (windStatusNo || '99') : undefined,
      windSpeedStatusLabel,
      windSpeedValue: hasWind ? (worstWind?.speed ?? latest?.siteWindSpeed) : undefined,
      windDirection: hasWind ? mapWindDirection(windDirectionCode) : undefined,
      windDirectionCode,
      windDate: hasWind ? worstWind?.date : undefined,
      noiseStatus,
      noiseStatusNo: hasNoise ? (noiseStatusNo || '99') : undefined,
      noiseStatusLabel,
      noiseSensors,
    };
  }

  public readonly sites = computed<Site[]>(() => {
    const rawSites = this.sitesResource.hasValue() ? this.sitesResource.value() : [];
    const role = this.authService.userRole();
    if (role === 'dep_admin') {
      const departmentId = this.authService.userDepartmentId();
      return rawSites.filter(site => site.managementDepartmentId === departmentId);
    }
    return rawSites;
  });

  public readonly siteListItems = computed<SiteListItem[]>(() => {
    const sites = this.sites();
    const latestData = this.latestSiteDataResource.hasValue() ? this.latestSiteDataResource.value() : [];
    const latestMap = new Map(latestData.map(d => [d.id, d]));
    const departments = this.departmentService.departments();
    const deptMap = new Map(departments.map(d => [d.id, d.name]));
    const allSensors = this.sensorService.allSensors();

    return sites.map(site => {
      const latest = latestMap.get(site.id);
      const siteSensors = allSensors.filter(s => s.siteId === site.id);
      const hasWbgt = siteSensors.some(s => s.categoryName === 'WBGT');
      const hasWind = siteSensors.some(s => s.categoryName === 'WIND');
      const hasNoise = siteSensors.some(s => s.categoryName === 'NOISE');
      const detail = this.mergeSiteWithLatestData(site, latest, hasWbgt, hasWind, hasNoise);
      return {
        id: detail.id,
        name: detail.name,
        address1: detail.address1,
        address2: detail.address2,
        managementDepartmentId: detail.managementDepartmentId,
        managementDepartmentName: detail.managementDepartmentId ? (deptMap.get(detail.managementDepartmentId) || undefined) : undefined,
        siteStatusNo: detail.siteStatusNo,
        wbgtStatus: detail.wbgtStatus,
        wbgtStatusNo: detail.wbgtStatusNo,
        wbgtStatusLabel: detail.wbgtStatusLabel,
        wbgtValue: detail.wbgtValue,
        windSpeedStatus: detail.windSpeedStatus,
        windSpeedStatusNo: detail.windSpeedStatusNo,
        windSpeedStatusLabel: detail.windSpeedStatusLabel,
        windSpeedValue: detail.windSpeedValue,
        windDirection: detail.windDirection,
        windDirectionCode: detail.windDirectionCode,
        geopoint: detail.geopoint
      } as SiteListItem;
    });
  });

  public readonly isSitesResolved = computed(() => {
    if (!this.authService.userOrganizationId() && this.authService.userRole() !== 'super_admin') return false;
    const status = this.sitesResource.status();
    return status === 'resolved' || status === 'error' || status === 'local';
  });

  public readonly isLoading = computed(() => 
    this.sitesResource.status() === 'loading' || 
    this.sitesResource.status() === 'reloading' ||
    this.latestSiteDataResource.status() === 'loading' ||
    this.latestSiteDataResource.status() === 'reloading'
  );

  public readonly error = computed(() => {
    const siteErr = this.sitesResource.error();
    const dataErr = this.latestSiteDataResource.error();
    if (siteErr) return siteErr instanceof Error ? siteErr.message : '現場一覧の取得エラー';
    if (dataErr) return dataErr instanceof Error ? dataErr.message : '最新データの取得エラー';
    return null;
  });

  public getSiteDetail(targetId: string | null): SiteDetail | undefined {
    if (!targetId) return undefined;

    const sites = this.sites();
    const site = sites.find(s => s.id === targetId);
    if (!site) return undefined;

    const latestData = this.latestSiteDataResource.hasValue() ? this.latestSiteDataResource.value() : [];
    const latest = latestData.find(d => d.id === targetId);

    const allSensors = this.sensorService.allSensors();
    const siteSensors = allSensors.filter(s => s.siteId === targetId);
    const hasWbgt = siteSensors.some(s => s.categoryName === 'WBGT');
    const hasWind = siteSensors.some(s => s.categoryName === 'WIND');
    const hasNoise = siteSensors.some(s => s.categoryName === 'NOISE');

    return this.mergeSiteWithLatestData(site, latest, hasWbgt, hasWind, hasNoise);
  }

  public getLatestSiteData(targetId: string | null): LatestSiteData | undefined {
    if (!targetId) return undefined;

    const latestData = this.latestSiteDataResource.hasValue() ? this.latestSiteDataResource.value() : [];
    return latestData.find(d => d.id === targetId);
  }

  public reloadSites(): void {
    this.sitesResource.reload();
  }

  public async createSite(data: SiteCreateInput & { siteId: string; password: string }, organizationId?: string): Promise<string> {
    const orgId = organizationId || this.authService.userOrganizationId();
    if (!orgId) throw new Error('組織IDが設定されていません');

    const createSiteFn = httpsCallable<{
      siteId: string;
      name: string;
      address1: string;
      address2?: string;
      geopoint?: any;
      managementDepartmentId?: string;
      organizationId: string;
      password: string;
    }, { siteId: string; uuid: string }>(this.functions, 'createSite');

    try {
      const result = await createSiteFn({
        ...data,
        organizationId: orgId
      });
      await this.logAction('site.create', `sites/${result.data.siteId}`, 'success');
      return result.data.siteId;
    } catch (err) {
      await this.logAction('site.create', 'sites', 'error');
      throw err;
    }
  }

  public async getSiteUuid(siteId: string): Promise<string | null> {
    const mapRef = collection(this.firestore, 'uuid_site_map');
    const q = query(mapRef, where('siteId', '==', siteId));
    const snapshot = await getDocs(q);

    if (snapshot.docs.length === 0) {
      return null;
    }

    if (snapshot.docs.length > 1) {
      throw new Error(`UUIDの紐付けが重複しています: ${siteId}`);
    }

    return snapshot.docs[0].id;
  }

  public async updateSite(id: string, data: SiteUpdateInput): Promise<void> {
    const siteRef = doc(this.firestore, 'sites', id);
    await updateDoc(siteRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
  }

  public async updateAlertSettings(siteId: string, settings: SiteAlertSettings, emails?: string[]): Promise<void> {
    const siteRef = doc(this.firestore, 'sites', siteId);
    const payload: UpdateData<Site> = {
      alertSettings: settings,
      updatedAt: serverTimestamp()
    };

    if (emails !== undefined) {
      payload.emails = emails.length > 0 ? emails : deleteField();
    }

    await updateDoc(siteRef, payload);
  }

  public async deleteSite(id: string): Promise<void> {
    try {
      // 1. 対象の現場に紐付くセンサーを検索
      const sensorsRef = collection(this.firestore, 'sensors');
      const sensorsQuery = query(sensorsRef, where('siteId', '==', id));
      const sensorsSnapshot = await getDocs(sensorsQuery);

      const siteRef = doc(this.firestore, 'sites', id);

      // Firestore の writeBatch 上限は 500 操作
      // 現場の updateDoc(1) + センサーの updateDoc(最大499) 単位で処理
      const MAX_BATCH_SIZE = 500;
      
      let batch = writeBatch(this.firestore);
      let operationCount = 0;

      // 現場の論理削除を最初のバッチに追加
      batch.update(siteRef, {
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      operationCount++;

      for (const sensorDoc of sensorsSnapshot.docs) {
        if (operationCount === MAX_BATCH_SIZE) {
          // 上限に達したらコミットし、新しいバッチを作成
          await batch.commit();
          batch = writeBatch(this.firestore);
          operationCount = 0;
        }

        const sRef = doc(this.firestore, 'sensors', sensorDoc.id);
        batch.update(sRef, {
          siteId: null,
          updatedAt: serverTimestamp()
        });
        operationCount++;
      }

      // 残りのバッチをコミット
      if (operationCount > 0) {
        await batch.commit();
      }
      await this.logAction('site.delete', `sites/${id}`, 'success');
    } catch (err) {
      console.error('現場の削除に失敗しました:', err);
      await this.logAction('site.delete', `sites/${id}`, 'error');
      throw new Error('現場の削除に失敗しました');
    }
  }
}
